import { useState, useMemo, useCallback } from "react";
import {
  useInfiniteQuery,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { usePymbleApi } from "./usePymbleApi";

const GROUPS_SEARCH_ENDPOINT = "/group/search";
const SUBSCRIBER_ENDPOINT = "/group/admin/subscribers";
const DEFAULT_GROUP_ICON =
  "https://pymblelc.blob.core.windows.net/assets/pymble-p.png";

/**
 * Server page size for /group/search. The API's `page` param is 1-based
 * (server skip = limit * (page - 1)).
 */
export const GROUPS_PAGE_SIZE = 60;

export interface BrowseFilter {
  search: string;
  active_only: boolean;
  type: number[];
  subscribedOnly: boolean;
}

export interface BrowseGroup {
  _id: string;
  Name?: { Short?: string; Long?: string };
  Description?: { Short?: string; Long?: string };
  Icon?: string;
  Type?: { Id?: number; Label?: string };
  StartAt?: string;
  EndAt?: string | null;
  MemberCount?: number;
  Subscribed?: boolean;
  [key: string]: unknown;
}

interface SubscribeRequest {
  _id: string;
  Subscribed: boolean;
}

/**
 * Hook for browse-mode group listing with subscription support.
 * Unlike useGroups (admin-oriented), this is optimized for the
 * user-facing browse page.
 *
 * Groups are fetched one server page at a time (limit/page on
 * /group/search) rather than all at once; `fetchNextPage` loads the next
 * page. `search`, `active_only` and `type` filters are server-side (a
 * change resets to page 1 via the query key); `subscribedOnly` filters
 * client-side across the pages fetched so far.
 */
export function useGroupBrowse() {
  const { callApi, isAuthenticated } = usePymbleApi();
  const queryClient = useQueryClient();

  const [filter, setFilter] = useState<BrowseFilter>({
    search: "",
    active_only: true,
    type: [],
    subscribedOnly: false,
  });

  // Fetch groups one server page at a time
  const groupsQuery = useInfiniteQuery({
    queryKey: ["browse-groups", filter.search, filter.active_only, filter.type],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (filter.search) params.set("search", filter.search);
      params.set("active_only", filter.active_only ? "1" : "0");
      if (filter.type.length > 0) params.set("type", filter.type.join(","));
      params.set("limit", String(GROUPS_PAGE_SIZE));
      params.set("page", String(pageParam));

      const res = await callApi(
        "GET",
        `${GROUPS_SEARCH_ENDPOINT}?${params.toString()}`
      );
      if (!res) return [];

      return (res as BrowseGroup[]).map((g) => ({
        ...g,
        Icon: g.Icon || DEFAULT_GROUP_ICON,
      }));
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.length === GROUPS_PAGE_SIZE ? lastPageParam + 1 : undefined,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5, // 5 min
  });

  // Fetch user's subscribable groups (returns groups the user IS subscribed to)
  const subscribedQuery = useQuery({
    queryKey: ["browse-subscribed"],
    queryFn: async () => {
      const res = await callApi("GET", SUBSCRIBER_ENDPOINT);
      if (!res) return [];
      return res as Array<{ _id?: string; GroupId?: string; Subscribed?: boolean; [key: string]: unknown }>;
    },
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5, // 5 min
  });

  // Build a set of subscribed group IDs for fast lookup
  const subscribedGroupIds = useMemo(() => {
    const ids = new Set<string>();
    if (subscribedQuery.data) {
      for (const sub of subscribedQuery.data) {
        if (sub.Subscribed !== false) {
          const gid = sub.GroupId || sub._id;
          if (gid) ids.add(gid);
        }
      }
    }
    return ids;
  }, [subscribedQuery.data]);

  // Merge subscription status into fetched pages and apply client-side filters.
  // The server sorts by Name.Short and pages arrive in order, so the
  // concatenated list is already sorted - no client re-sort (one could
  // disagree with the server collation and shuffle rows across pages).
  const groups = useMemo(() => {
    const pages = groupsQuery.data?.pages ?? [];

    let result = pages.flat().map((g) => ({
      ...g,
      Subscribed: subscribedGroupIds.has(g._id),
    }));

    if (filter.subscribedOnly) {
      result = result.filter((g) => g.Subscribed);
    }

    return result;
  }, [groupsQuery.data, subscribedGroupIds, filter.subscribedOnly]);

  // Subscribe/unsubscribe mutation
  const subscribeMutation = useMutation({
    mutationFn: async (req: SubscribeRequest) => {
      return await callApi("POST", SUBSCRIBER_ENDPOINT, { data: req });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["browse-subscribed"] });
    },
  });

  const toggleSubscription = (groupId: string) => {
    const isSubscribed = subscribedGroupIds.has(groupId);
    subscribeMutation.mutate({
      _id: groupId,
      Subscribed: !isSubscribed,
    });
  };

  const isLoading = groupsQuery.isLoading || subscribedQuery.isLoading;

  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = groupsQuery;

  // Load the next server page. cancelRefetch: false makes a repeated call
  // while a page fetch is in flight a no-op instead of cancelling and
  // restarting it (the scroll sentinel and the Load More button can both
  // fire before a rerender lands).
  const loadMore = useCallback(() => {
    if (!hasNextPage) return;
    void fetchNextPage({ cancelRefetch: false });
  }, [fetchNextPage, hasNextPage]);

  return {
    groups,
    isLoading,
    filter,
    setFilter,
    toggleSubscription,
    isSubscribing: subscribeMutation.isPending,
    loadMore,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    refreshGroups: () => {
      queryClient.invalidateQueries({ queryKey: ["browse-groups"] });
      queryClient.invalidateQueries({ queryKey: ["browse-subscribed"] });
    },
  };
}
